import cn from 'classnames';
import { FC, KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { TEXT_TOOLTIP_DELAY_DURATION_MS } from '@/shared/config';
import { useIsOverflowing } from '@/shared/hooks';
import { getLanguageDisplayName } from '@/shared/lib';
import { Icon, Tooltip } from '@/shared/ui';

import { getDocumentTypeLabel, getSpecialtyLabel } from '@/entities/templates';

import styles from './CommunityAssetCard.module.scss';

type CommunityAssetCardProps = {
  asset: RegistryAsset;
  className?: string;
  onPreview: (asset: RegistryAsset) => void;
};

export const CommunityAssetCard: FC<CommunityAssetCardProps> = (props) => {
  const { asset, className, onPreview } = props;
  const { formatMessage, formatNumber, locale } = useIntl();

  const {
    name,
    originKind,
    hasLocalClone,
    language,
    templateType,
    sectionsCount,
    authorDisplayName,
    authorSpecialty,
    importCount,
  } = asset;

  const isOfficial = originKind === 'official';
  const languageLabel = getLanguageDisplayName(language, locale);
  const description =
    asset.description ??
    formatMessage({
      id: 'communityCatalog.cardNoDescription',
      defaultMessage: 'No description available',
    });

  const { isOverflowing: isNameTruncated, ref: nameRef } =
    useIsOverflowing<HTMLHeadingElement>(name);
  const { isOverflowing: isDescriptionTruncated, ref: descriptionRef } =
    useIsOverflowing<HTMLParagraphElement>(description);

  const handleClick = () => {
    onPreview(asset);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onPreview(asset);
    }
  };

  const nameNode = (
    <h3 ref={nameRef} className={cn('h5', styles.name)}>
      {name}
    </h3>
  );

  const descriptionNode = (
    <p
      ref={descriptionRef}
      className={cn(
        'p3',
        styles.description,
        !authorDisplayName && styles.descriptionWithoutAttribution,
      )}
    >
      {description}
    </p>
  );

  return (
    <div
      className={cn(styles.container, className)}
      data-component="community-asset-card"
      role="button"
      tabIndex={0}
      aria-label={name}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.header}>
        <span
          className={cn(
            'p4',
            styles.badge,
            isOfficial ? styles.badgeOfficial : styles.badgeCommunity,
          )}
        >
          {isOfficial
            ? formatMessage({
                id: 'communityCatalog.cardBadgeOfficial',
                defaultMessage: 'Official',
              })
            : formatMessage({
                id: 'communityCatalog.cardBadgeCommunity',
                defaultMessage: 'Community',
              })}
        </span>
        {languageLabel && (
          <span className={cn('p4', styles.badge, styles.badgeLanguage)}>
            {languageLabel}
          </span>
        )}
        {hasLocalClone && (
          <span className={cn('p4', styles.badge, styles.badgeImported)}>
            {formatMessage({
              id: 'communityCatalog.cardBadgeAlreadyImported',
              defaultMessage: 'Added',
            })}
          </span>
        )}
      </div>

      <div className={styles.main}>
        {isNameTruncated ? (
          <Tooltip.Primary
            content={name}
            className={styles.tooltipContent}
            delayDuration={TEXT_TOOLTIP_DELAY_DURATION_MS}
            triggerClassName={styles.textTooltipTrigger}
          >
            {nameNode}
          </Tooltip.Primary>
        ) : (
          nameNode
        )}

        {isDescriptionTruncated ? (
          <Tooltip.Primary
            content={description}
            className={styles.tooltipContent}
            delayDuration={TEXT_TOOLTIP_DELAY_DURATION_MS}
            triggerClassName={styles.textTooltipTrigger}
          >
            {descriptionNode}
          </Tooltip.Primary>
        ) : (
          descriptionNode
        )}

        <div className={styles.meta}>
          <span className={cn('p4', styles.metaChip)}>
            {templateType
              ? getDocumentTypeLabel(formatMessage, templateType)
              : formatMessage({
                  id: 'global.document',
                  defaultMessage: 'Document',
                })}
          </span>
          {sectionsCount !== null && (
            <span className={cn('p4', styles.metaChip)}>
              {formatMessage(
                {
                  id: 'communityCatalog.cardMetaSections',
                  defaultMessage:
                    '{count, plural, one {# section} other {# sections}}',
                },
                { count: sectionsCount },
              )}
            </span>
          )}
        </div>
      </div>

      {authorDisplayName && (
        <div className={styles.attribution}>
          <span className={styles.avatar}>
            <Icon name="user" size={16} />
          </span>
          <span className={styles.author}>
            <span className={cn('p4b', styles.authorName)}>
              {authorDisplayName}
            </span>
            {authorSpecialty && (
              <span className={cn('p4', styles.authorSpecialty)}>
                {getSpecialtyLabel(formatMessage, authorSpecialty)}
              </span>
            )}
          </span>
          {importCount !== null && (
            <span className={cn('p4', styles.imports)}>
              <Icon name="download" size={16} />
              {formatNumber(importCount)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
